import React, { Fragment } from 'react';
import Localize from 'react-intl-universal';

import { isEmpty } from 'lodash-es';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { formatPrice } from '@common/Util';
import NoContent from '@components/NoContentComponent/NoContent.jsx';
import { getCurrency } from '@pages/EventDetails/Util';

import OrderSummaryItem from './OrderSummaryItem';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginTop: '1rem',
  marginBottom: '1rem',
  boxShadow: 'unset',
  backgroundColor: theme.palette.booking.info,
  '&.Mui-expanded': {
    '&:last-of-type': {
      marginBottom: '1rem'
    }
  }
}));

const StyledPriceContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '3px'
}));

const StyledTotalItem = styled(Typography)(() => ({
  textAlign: 'left',
  fontSize: '15px',
  fontWeight: 'bold',
  paddingLeft: '0.8rem'
}));

const StyledSubTotalItem = styled(Typography)(() => ({
  textAlign: 'left',
  paddingLeft: '0.8rem'
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  fontWeight: '700',
  '& .MuiAccordionSummary-content': {
    display: 'block',
    textAlign: 'left',
    paddingLeft: '1rem'
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.light
  }
}));

const StyledGrid = styled(Grid)(() => ({
  '& .MuiTypography-root': {
    fontSize: '14px'
  }
}));

const StyledSubheader = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontWeight: '400',
  fontSize: '0.85rem'
}));

const StyledPrice = styled(Typography)(() => ({
  display: 'inline',
  paddingLeft: '2px'
}));

const StyledTotal = styled(Box)(({ theme }) => ({
  borderRadius: '2px',
  backgroundColor: theme.palette.booking.total,
  padding: '0.75rem',
  marginBottom: '0.75rem',
  '& .MuiTypography-root': {
    color: theme.palette.booking.header,
    fontFamily: `${theme.typography.special.fontFamily} !important`
  }
}));

const OrderSummary = ({ bookingData = {}, eventName = '' }) => {
  const { bookedParticipants = [], totalBookedPriceWithoutTax = '' } = bookingData;

  const getMandatorySessions = (sessionParticipants = []) => {
    return sessionParticipants.filter(({ session }) => !session.optionalSession);
  };

  const getOptionalSessions = (sessionParticipants = []) => {
    return sessionParticipants
      .map((sp, index) => ({ ...sp, index }))
      .filter(({ session }) => Boolean(session.optionalSession));
  };

  const discountLabel = (attendee) =>
    `${attendee?.discounts[0]?.name}: ${attendee?.discounts[0]?.value} ${attendee?.discounts[0].unitType?.key === 'FIXED_PRICE' ? attendee?.currency?.value : '%'} `;

  return (
    <>
      {isEmpty(bookedParticipants) && (
        <Box data-testid={'noComponentMessage'}>
          <NoContent infoType={Localize.get('EventDetails.noBookedParticipants')} />
        </Box>
      )}
      {bookedParticipants.map(({ attendee = {}, sessionParticipants = [] }, participantIndex) => (
        <Box key={`${Localize.get('Booking.participant')}-${participantIndex}}`}>
          <StyledAccordion square={true} defaultExpanded={participantIndex === 0}>
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={participantIndex}
              id={participantIndex}
            >
              <Box>
                {attendee?.firstName} {attendee?.lastName}
              </Box>
              <StyledSubheader> {Localize.get('Booking.participant')}</StyledSubheader>
            </StyledAccordionSummary>

            <AccordionDetails>
              <Grid key={participantIndex} container>
                {/* Event Name & Standard Price */}
                <Grid container item mb={1}>
                  <Grid item xs={1}>
                    <Typography display='inline'>{'1x '}</Typography>
                  </Grid>
                  <Grid item xs={7} sx={{ overflowWrap: 'anywhere', textAlign: 'left' }}>
                    <Box display='inline'>
                      {eventName}
                      <Typography fontSize='12px' sx={{ color: 'text.secondary' }}>
                        {attendee?.status.value}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}>
                    <StyledPriceContainer>
                      {formatPrice(attendee?.standardPrice || 0)}{' '}
                      <StyledPrice>{getCurrency(bookedParticipants)}</StyledPrice>
                    </StyledPriceContainer>
                  </Grid>
                </Grid>
                <Grid
                  container
                  rowSpacing={1}
                  sx={{
                    fontSize: '0.8rem',
                    opacity: 0.7,
                    marginBottom: '0.7rem',
                    paddingLeft: '1rem'
                  }}
                >
                  {/* Attendee Discounts */}
                  {attendee?.discounts[0] && (
                    <OrderSummaryItem
                      label={'Booking.discounts'}
                      itemName={discountLabel(attendee)}
                      itemValue={`-${(attendee?.discountsValue || 0).toFixed(2)}`}
                      bookedParticipants={bookedParticipants}
                      no={'1x'}
                    />
                  )}

                  {/* Attendee Mandatory Sessions */}
                  <Grid item xs={1}></Grid>
                  <Grid item xs={11} sx={{ textAlign: 'left' }}>
                    {getMandatorySessions(sessionParticipants).map(({ session }) => (
                      <Box
                        sx={(theme) => ({
                          color: theme.palette.primary.contrastText,
                          fontSize: '1rem'
                        })}
                        key={session?.id}
                      >
                        {session?.name}
                        <Typography display='inline' fontSize='12px' sx={{ pl: 1, pr: 1 }}>
                          ({Localize.get('Booking.mandatory')})
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                </Grid>
                {/* Attendee Optional Sessions */}
                {getOptionalSessions(sessionParticipants).map(
                  (sessionParticipant, sessionIndex) => (
                    <Fragment key={sessionIndex}>
                      {/* Event Name & Standard Price */}
                      <Grid container item mb={1}>
                        <Grid item xs={1}>
                          <Typography display='inline'>{'1x '}</Typography>
                        </Grid>
                        <Grid item xs={7} sx={{ overflowWrap: 'anywhere', textAlign: 'left' }}>
                          <Box
                            sx={(theme) => ({
                              color: theme.palette.primary.contrastText,
                              fontSize: '1rem'
                            })}
                            key={sessionParticipant.session?.id}
                          >
                            {sessionParticipant?.session?.name}
                            <Typography
                              display='inline'
                              fontSize='12px'
                              sx={{ color: 'text.secondary', pl: 1, pr: 1 }}
                            >
                              ({Localize.get('Booking.optional')})
                            </Typography>
                            <Typography color='text.secondary' fontSize='12px'>
                              {sessionParticipant?.status?.value}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}
                        >
                          <StyledPriceContainer>
                            {formatPrice(sessionParticipant?.standardPrice || 0)}
                            <StyledPrice>{getCurrency(bookedParticipants)}</StyledPrice>
                          </StyledPriceContainer>
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        rowSpacing={1}
                        sx={{
                          fontSize: '0.8rem',
                          opacity: 0.7,
                          marginBottom: '0.7rem',
                          paddingLeft: '1rem'
                        }}
                      >
                        {/* Session Discounts */}
                        {sessionParticipant?.discounts[0]?.name && (
                          <OrderSummaryItem
                            label={'Booking.discounts'}
                            itemName={discountLabel(sessionParticipant)}
                            itemValue={`-${(sessionParticipant?.discountsValue || 0).toFixed(2)}`}
                            bookedParticipants={bookedParticipants}
                            no={'1x'}
                          />
                        )}
                      </Grid>
                    </Fragment>
                  )
                )}
              </Grid>
              {/* Subtotal */}
              <Divider />
              <StyledGrid container sx={{ mt: 1, mb: 1 }}>
                <Grid item xs={5} sx={{ alignContent: 'center' }}>
                  <StyledSubTotalItem sx={{ justifyContent: 'left', fontSize: '15px' }}>
                    {Localize.get('Booking.AmountForBookedParticipants')}
                  </StyledSubTotalItem>
                </Grid>
                <Grid item xs={7} sx={{ textAlign: 'end', alignContent: 'center' }}>
                  <StyledPriceContainer>
                    {formatPrice(attendee?.totalPossiblePriceWithoutTax || 0)}
                    <StyledPrice>{getCurrency(bookedParticipants)}</StyledPrice>
                  </StyledPriceContainer>
                </Grid>
              </StyledGrid>
              <Divider />
            </AccordionDetails>
          </StyledAccordion>
        </Box>
      ))}
      {!isEmpty(bookedParticipants) && (
        <StyledTotal>
          {/* Total */}
          <Grid container sx={{ mt: 1, mb: 1 }}>
            <Grid item xs={5}>
              <StyledTotalItem>
                {Localize.get('Booking.TotalPriceForBookedParticipants')}
              </StyledTotalItem>
            </Grid>
            <Grid item xs={7} sx={{ textAlign: 'end', alignContent: 'center' }}>
              <StyledPriceContainer sx={{ fontWeight: 'bold' }}>
                {formatPrice(totalBookedPriceWithoutTax || 0)}
                <StyledPrice>{getCurrency(bookedParticipants)}</StyledPrice>
              </StyledPriceContainer>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1, mb: 1, paddingLeft: '0.8rem' }}>
              <Typography>{Localize.get('Booking.vatNotIncluded')}</Typography>
            </Grid>
          </Grid>
        </StyledTotal>
      )}
    </>
  );
};
export default OrderSummary;
